import { getCurrentApi } from './baseAPI';

class PositionAPI {
  // Get aggregated positions
  async getAggregatedPositions() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/positions/aggregated');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch aggregated positions:', error);
      throw error;
    }
  }

  // Get net position for instrument
  async getNetPosition(symbol) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/positions/instruments/${symbol}/net`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch net position for ${symbol}:`, error);
      throw error;
    }
  }

  // Get all instrument positions
  async getAllInstrumentPositions() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/positions/instruments');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch all instrument positions:', error);
      throw error;
    }
  }

  // Get positions summary
  async getPositionsSummary() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/positions/summary');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch positions summary:', error);
      throw error;
    }
  }

  // Get user positions
  async getUserPositions(userId) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/positions/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch positions for user ${userId}:`, error);
      throw error;
    }
  }

  // Get user orders
  async getUserOrders(userId) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/positions/users/${userId}/orders`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch orders for user ${userId}:`, error);
      throw error;
    }
  }

  // Get positions by user
  async getPositionsByUser() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/positions/by-user');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch positions by user:', error);
      throw error;
    }
  }

  // Get risk exposure
  async getRiskExposure() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/positions/risk-exposure');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch risk exposure:', error);
      throw error;
    }
  }

  // Admin: Close a trade (close position)
  async closeTrade(userId, tradeId, closeData = {}) {
    try {
      const api = await getCurrentApi();
      const response = await api.post('/positions/close', {
        userId,
        tradeId,
        closePercentage: closeData.closePercentage ?? 100,
        closeQuantity: closeData.closeQuantity,
        description: closeData.description || 'Closed by admin',
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to close trade ${tradeId} for user ${userId}:`, error);
      throw error;
    }
  }

  // Admin: Remove an order (only orders not executed by vendor)
  async removeOrder(userId, orderId, reason = 'Removed by admin') {
    try {
      const api = await getCurrentApi();
      const response = await api.post('/positions/orders/remove', {
        userId,
        orderId,
        reason,
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to remove order ${orderId} for user ${userId}:`, error);
      throw error;
    }
  }

  // Admin: Add / Force-Open a trade for a user
  async addTrade(tradeData) {
    try {
      const api = await getCurrentApi();
      const response = await api.post('/positions/add', {
        userId: tradeData.userId,
        symbol: tradeData.symbol,
        side: tradeData.side,
        quantity: Number(tradeData.quantity),
        price: Number(tradeData.price),
        timestamp: tradeData.timestamp || undefined,
        description: tradeData.description || 'Trade added by admin',
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to add trade for user ${tradeData.userId}:`, error);
      throw error;
    }
  }

  // Admin: Modify an existing trade's SL/TP/Quantity
  async modifyTrade(userId, tradeId, modifyData = {}) {
    try {
      const api = await getCurrentApi();
      const body = {
        userId,
        tradeId,
        timestamp: modifyData.timestamp || undefined,
        description: modifyData.description || 'Trade modified by admin',
      };
      if (modifyData.takeProfit !== undefined) {
        body.takeProfit = modifyData.takeProfit != null ? Number(modifyData.takeProfit) : null;
      }
      if (modifyData.stopLoss !== undefined) {
        body.stopLoss = modifyData.stopLoss != null ? Number(modifyData.stopLoss) : null;
      }
      if (modifyData.quantity !== undefined) {
        body.quantity = Number(modifyData.quantity);
      }
      if (modifyData.entryPrice !== undefined) {
        body.entryPrice = Number(modifyData.entryPrice);
      }
      const response = await api.post('/positions/modify', body);
      return response.data;
    } catch (error) {
      console.error(`Failed to modify trade ${tradeId} for user ${userId}:`, error);
      throw error;
    }
  }

  // Get position history
  async getPositionHistory(options = {}) {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();
      
      if (options.userId) params.append('userId', options.userId);
      if (options.symbol) params.append('symbol', options.symbol);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);

      const queryString = params.toString();
      const url = queryString ? `/positions/history?${queryString}` : '/positions/history';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch position history:', error);
      throw error;
    }
  }
}

export const positionAPI = new PositionAPI();
export default positionAPI;
